import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking } from 'react-native';
import { useAppTheme } from '../../utils/useAppTheme';

const blogHighlights = [
  {
    title: 'Alphanumeric Qabbala Cipher: Unveiling the Mystical Base-36 System',
    date: 'May 15, 2025',
    author: 'Dr. Jonathan Mercer',
    excerpt: 'In the vast landscape of esoteric knowledge and mystical traditions, numerical systems have long served as bridges between the mundane and the divine. Alphanumeric Qabbala represents a unique intersection of modern computational concepts and ancient mystical traditions, using a base-36 system that assigns values to both letters and digits.',
    url: '/pages/blog-posts/alphanumeric-qabbala-cipher.html'
  },
  {
    title: 'Unlocking the Mysteries of Gematria: The Ancient Art of Hebrew Numerical Wisdom',
    date: 'March 5, 2025',
    author: 'David Rosenberg',
    excerpt: 'Have you ever wondered if there is more to sacred texts than meets the eye? Gematria is a Kabbalistic method of interpreting Hebrew scriptures by computing the numerical value of words based on the values of their constituent letters. This article traces the practice from its Talmudic origins through medieval Kabbalah to its modern revival.',
    url: '/pages/blog-posts/unlocking-the-mysteries-of-gematria.html'
  },
  {
    title: 'Gematria Across Cultures: From Hebrew to Greek and Beyond',
    date: 'February 28, 2025',
    author: 'Sarah Johnson',
    excerpt: 'While gematria is most commonly associated with Hebrew texts and Jewish mysticism, similar numerological systems have appeared in cultures around the world. From Greek isopsephy to Arabic Abjad numerals, this cross-cultural phenomenon raises fascinating questions about the universal human impulse to find meaning in language and number.',
    url: '/pages/blog-posts/gematria-across-cultures.html'
  },
  {
    title: 'Practical Applications of Gematria in Modern Life',
    date: 'February 10, 2025',
    author: 'David Goldstein',
    excerpt: 'Is gematria merely an ancient curiosity, or can it offer practical value in our modern lives? From naming businesses to personal reflection and creative problem-solving, this article explores how people apply gematria principles in everyday contexts far removed from the study hall.',
    url: '/pages/blog-posts/practical-applications.html'
  },
  {
    title: 'The Mathematics Behind Gematria: Number Theory and Sacred Geometry',
    date: 'January 25, 2025',
    author: 'Robert Chen',
    excerpt: 'Beyond its spiritual and mystical applications, gematria represents a fascinating intersection of mathematics, language, and philosophy. This article explores the mathematical principles underlying gematria, including modular arithmetic, number theory, and the geometric patterns that emerge from numerical letter values.',
    url: '/pages/blog-posts/mathematics-behind-gematria.html'
  },
  {
    title: "Debunking Myths: What Gematria Is and Isn't",
    date: 'January 12, 2025',
    author: 'Rachel Levy',
    excerpt: "With the growing popularity of gematria in mainstream culture, misconceptions have become increasingly common. This article clarifies what gematria truly is — a structured interpretive tradition with deep historical roots — and what it is not, separating legitimate scholarship from sensationalized claims.",
    url: '/pages/blog-posts/debunking-myths.html'
  },
  {
    title: 'The Hidden Connections: How Gematria Reveals Patterns in Sacred Texts',
    date: 'May 15, 2021',
    author: 'Michael Cohen',
    excerpt: 'For thousands of years, scholars and mystics have used gematria to uncover hidden connections within sacred texts. These numerical relationships often reveal surprising patterns that are not apparent from the surface reading alone, adding layers of meaning to passages studied for millennia.',
    url: '/pages/blog-posts/hidden-connections.html'
  },
  {
    title: 'Kabbalah Numerology: The Divine Mathematics of Jewish Mysticism',
    date: 'August 29, 2002',
    author: 'Dr. Arturo Contreras',
    excerpt: 'Have you ever wondered if the universe speaks in numbers? In the ancient tradition of Kabbalah, numbers are not just quantities — they are gateways to understanding the divine architecture of creation itself. This article explores how Kabbalistic numerology uses gematria to map the structure of spiritual reality.',
    url: '/pages/blog-posts/kabbalah-numerology.html'
  },
  {
    title: 'Related Numerical Mystical Systems: A Comparative Study',
    date: 'December 13, 2017',
    author: 'Rebekah Galindo',
    excerpt: 'Throughout history, cultures across the world have developed sophisticated systems for understanding the universe through numbers. This exploration focuses on Pythagorean numerology, Islamic Abjad numerology, Christian Cabala, and Hermetic Qabalah.',
    url: '/pages/blog-posts/numerical-mystical-systems.html'
  },
  {
    title: 'The Mathematical Mysticism of Hebrew Letters',
    date: 'July 22, 2013',
    author: 'Dr. Raphael McFarland',
    excerpt: 'What continues to amaze scholars about gematria is how it embodies a worldview where mathematics and spirituality are not separate domains but interconnected aspects of a unified reality. This article examines the philosophical foundations of treating letters as numbers and numbers as carriers of meaning.',
    url: '/pages/blog-posts/mathematical-mysticism.html'
  },
];

export default function HomepageContent({ isMobile }) {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [expanded, setExpanded] = useState(false);

  return (
    <View style={[styles.container, isMobile && styles.containerMobile]}>
      <TouchableOpacity
        style={styles.toggleBar}
        onPress={() => setExpanded(!expanded)}
        accessibilityRole="button"
        accessibilityLabel={expanded ? 'Collapse gematria guide and articles' : 'Expand gematria guide and articles'}
      >
        <Text style={styles.toggleText}>
          {expanded ? '\u25BC' : '\u25B6'} About Gematria & Featured Articles
        </Text>
      </TouchableOpacity>

      {expanded && (
        <View style={styles.content}>

          {/* ===== WHAT IS GEMATRIA ===== */}
          <Text style={styles.sectionTitle}>What is Gematria?</Text>
          <Text style={styles.paragraph}>
            Gematria is a system of assigning numerical values to words or phrases in order to find hidden meanings or associations between words. This system is used in various ways by different cultures and traditions, including Jewish, Islamic, and early Christian practices.
          </Text>
          <Text style={styles.paragraph}>
            In Jewish gematria, each letter of the Hebrew alphabet is assigned a numerical value, and the sum of the values of the letters in a word is used to find relationships between words or to uncover hidden meanings in texts. For example, the Hebrew word for "truth" (emet) has a numerical value of 441, which is the same as the value of the word "death" (mavet). This coincidence is thought to indicate a connection between truth and death in Jewish thought.
          </Text>
          <Text style={styles.paragraph}>
            Islamic gematria is similar to Jewish gematria, but it uses the Arabic alphabet instead of the Hebrew alphabet. The system is used in a variety of ways in Islamic tradition, including for divination and for finding hidden meanings in the Quran.
          </Text>
          <Text style={styles.paragraph}>
            Christian gematria, also known as Isopsephy, is a system used by early Christians to find hidden meanings in the Bible. It involves assigning numerical values to letters in the Greek alphabet and using those values to find connections between words or to uncover hidden meanings in texts.
          </Text>
          <Text style={styles.paragraph}>
            Overall, gematria is a system that has been used for centuries to find connections and uncover hidden meanings in texts, and it continues to be used by a variety of cultures and traditions today.
          </Text>

          {/* ===== WHY PEOPLE USE IT ===== */}
          <Text style={styles.sectionTitle}>Why People Are Using the Gematria Calculator?</Text>
          <Text style={styles.paragraph}>
            People may use a gematria calculator for a variety of reasons. Some may be interested in using gematria to find hidden meanings or connections between words, while others may be interested in exploring the relationship between language and numerology.
          </Text>
          <Text style={styles.paragraph}>
            Gematria calculators can be helpful for those who are interested in gematria because they allow users to easily and quickly find the numerical values of words or phrases. This can be especially useful for those who are not familiar with the system, as calculating gematria values by hand can be time-consuming and require a good understanding of the system.
          </Text>
          <Text style={styles.paragraph}>
            Gematria calculators can also be used as a tool for divination or as a means of exploring the connections between language and numerology. Some people may use gematria to find spiritual or personal insights, or to gain a deeper understanding of texts or concepts.
          </Text>
          <Text style={styles.paragraph}>
            Overall, the use of gematria calculators varies depending on the individual and their motivations for using the tool.
          </Text>

          {/* ===== HOW TO CALCULATE ===== */}
          <Text style={styles.sectionTitle}>How Do You Calculate Gematria?</Text>
          <Text style={styles.paragraph}>
            There are a few different ways to calculate gematria, as the specific method used can vary depending on the tradition or culture using the system. Here are some general steps for calculating gematria values:
          </Text>
          <Text style={styles.paragraph}>
            1. Choose a language or alphabet to use. Gematria can be used with any language or alphabet, so you will need to decide which one you want to use for your calculations.
          </Text>
          <Text style={styles.paragraph}>
            2. Assign numerical values to each letter of the alphabet. Each letter of the alphabet is assigned a specific numerical value, which can vary depending on the system being used. For example, in the Hebrew gematria system, the letter aleph is assigned the value of 1, while the letter bet is assigned the value of 2.
          </Text>
          <Text style={styles.paragraph}>
            3. Write out the word or phrase that you want to calculate the gematria value for. Make sure to use the correct alphabet or script for the language that you have chosen.
          </Text>
          <Text style={styles.paragraph}>
            4. Find the numerical values of each letter in the word or phrase. Look up the numerical value of each letter in the alphabet, and write down the value next to the letter.
          </Text>
          <Text style={styles.paragraph}>
            5. Add up the numerical values of all of the letters in the word or phrase. The gematria value of the word or phrase is the sum of the values of all of the letters.
          </Text>
          <Text style={styles.paragraph}>
            For example, if we wanted to calculate the gematria value of the word "truth" in Hebrew, we would assign: Aleph = 1, Bet = 2, Tav = 400, Resh = 200, He = 5. Then add: 1 + 2 + 400 + 200 + 5 = 608. So the gematria value of the word "truth" in Hebrew is 608. Note that this is just a general example, and the specific values and methods used for calculating gematria can vary depending on the tradition or culture using the system.
          </Text>

          {/* ===== GEMATRIA NUMBER FOR JESUS ===== */}
          <Text style={styles.sectionTitle}>What is the Gematria Number for Jesus?</Text>
          <Text style={styles.paragraph}>
            In the Hebrew gematria system, the name "Jesus" has a numerical value of 358. This value is derived by assigning numerical values to each letter of the Hebrew alphabet and adding up the values of the letters in the name "Jesus."
          </Text>
          <Text style={styles.paragraph}>
            In Hebrew, the name "Jesus" is written as ישוע (yeshua), which consists of the following letters: Yod = 10, Shin = 300, Vav = 6, Ayin = 70. Adding these up gives a total of 386, which is the gematria value of the name "Jesus" in Hebrew.
          </Text>
          <Text style={styles.paragraph}>
            It's important to note that gematria values can vary depending on the language and alphabet being used, as well as the specific system of gematria being employed. The gematria value of the name "Jesus" may be different in other languages or using different gematria systems.
          </Text>

          {/* ===== FUNCTIONS ===== */}
          <Text style={styles.sectionTitle}>Gematria Calculator and Working of its Functions</Text>
          <Text style={styles.paragraph}>
            Gematria is a calculator which has a set of numbers and then converts letters into numbers according to that set of numbers. This calculator also has an ancient version which is called a biblical gematria. It has the special transformation function from letter to numbers; this word "Gematria" is mostly used for Hebrew.
          </Text>
          <Text style={styles.paragraph}>
            Before using the Gematria calculator we have to understand some functions of this calculator and also how all those functions work. To use a Gematria calculator tool we should know all these functions discussed below.
          </Text>

          {/* ===== PYTHAGOREANS ===== */}
          <Text style={styles.sectionTitle}>Pythagoreans</Text>
          <Text style={styles.paragraph}>
            First of all, we will discuss all the parts of this Cipher. These are all simple and easy to use and calculate the numerology in the Gematria calculator.
          </Text>

          <Text style={styles.subTitle}>Full Reduction</Text>
          <Text style={styles.paragraph}>
            It is a very simple Cipher to use. It has an alphabet table from A to Z and in this table, it has different number codes: A to I = 1 to 9, then J = 10 so it adds both digits 1+0 = 1, up to R where R = 14 and its code in Gematria Calculator is 9.
          </Text>
          <Text style={styles.paragraph}>
            Example: when we type RAM in the calculator it shows its value is 14. R=9, A=1, M=4, by adding all these codes we get the value 18.
          </Text>

          <Text style={styles.subTitle}>Single Reduction</Text>
          <Text style={styles.paragraph}>
            The Single Reduction is also a basic Cipher of the Gematria Calculator. It also works like Full Reduction but there is a little difference in the second row of alphabet codes: it has A to I and J to R with number codes from 1 to 9, but in this Cipher "S" has the number code as in the alphabetic order which is 19.
          </Text>
          <Text style={styles.paragraph}>
            Example: TOM = 12. T=2, O=6, M=4. (2+6+4=12) — 12 is the final code for the word "TOM".
          </Text>

          <Text style={styles.subTitle}>Reverse Full Reduction</Text>
          <Text style={styles.paragraph}>
            It is a very simple Cipher but we have to pay attention to the order of the alphabet in this Cipher of the Gematria Calculator, as it follows reverse order from A to Z where all the alphabets have different number codes: Z to R = 1 to 9, and Q to I = 1 to 9, but H = 10 and then G to A = 2 to 8.
          </Text>
          <Text style={styles.paragraph}>
            Example: TOS = 18. T=7, O=3, S=8. (7+3+8=18)
          </Text>

          <Text style={styles.subTitle}>Reverse Single Reduction</Text>
          <Text style={styles.paragraph}>
            This is a very different Cipher of the Gematria Calculator and its order is also different. It has number codes from A to J = 8 to 2 and H = 10, but I to Q = 9 to 1, R to Z = 9 to 1.
          </Text>
          <Text style={styles.paragraph}>
            Example: VAN = 17. V=5, A=8, N=4. (5+8+4=17)
          </Text>

          {/* ===== ALPHABETIC ORDER ===== */}
          <Text style={styles.sectionTitle}>Alphabetic Order</Text>
          <Text style={styles.paragraph}>
            Now, we will discuss all the Ciphers of alphabetic order. It also has many parts — some are basic and some are advanced. This is also a part of the Gematria calculator.
          </Text>

          <Text style={styles.subTitle}>English Ordinal</Text>
          <Text style={styles.paragraph}>
            This Cipher is also a basic Cipher in the Gematria Calculator. It works like its name — simple and easy to use. It has alphabetical order from 1 to 26, same as in the alphabet.
          </Text>
          <Text style={styles.paragraph}>
            Example: As we type in Gematria Calculator, the word "POP" = 47 is its code. In alphabetical order P=16, O=15, P=16, then 16+15+16=47, which is the code for "POP".
          </Text>

          <Text style={styles.subTitle}>Reverse Ordinal</Text>
          <Text style={styles.paragraph}>
            In the Gematria Calculator, it is a very different and basic Cipher. In this Cipher the alphabet order is from A to Z but number codes are in reverse form: A=26 and Z=1.
          </Text>
          <Text style={styles.paragraph}>
            Example: We type "BOS" — its code shown in the Gematria Calculator is 45. In reverse form B=25, O=12, S=8. By adding all: 25+12+8=45.
          </Text>

          <Text style={styles.subTitle}>Francis Bacons</Text>
          <Text style={styles.paragraph}>
            The Gematria Calculator contains many Ciphers — some basic and some advanced. Francis Bacons is an advanced Cipher. It has a special code for every alphabet. In this Cipher, small letters from a to z have number codes from 1 to 26, and capital letters A to Z have codes from 27 to 52.
          </Text>
          <Text style={styles.paragraph}>
            Example: Aa = 28. A=27, a=1. (27+1=28)
          </Text>

          <Text style={styles.subTitle}>English Extended</Text>
          <Text style={styles.paragraph}>
            This Cipher of the Gematria Calculator is very different from all the above Ciphers, as it has special number codes after every 8th and 9th alphabet. It has codes from A to I = 1 to 9, and then J to R = 10 to 90, codes increase by 10 after each step. The next line starts from hundreds and increases by 100 also.
          </Text>
          <Text style={styles.paragraph}>
            Example: As we type AJS in the calculator, this code will be calculated as 111. A=1, J=10, S=100. (1+10+100=111)
          </Text>

          <Text style={styles.subTitle}>Franc Bacons</Text>
          <Text style={styles.paragraph}>
            Franc Bacons also has different codes for every small letter and capital letter of the alphabet. In the alphabetic table of this Cipher, the alphabet is written with its codes as: A, a = 1, 2 and B, b = 3, 4.
          </Text>
          <Text style={styles.paragraph}>
            Example: As = 39. A=1, S=38. (1+38=39)
          </Text>

          <Text style={styles.subTitle}>Sumerian</Text>
          <Text style={styles.paragraph}>
            Sumerian is an advanced Cipher in the Gematria Calculator. As we type A it gives the value 6 because its number codes start from 6, and then in the next character it adds the order value: B=12, C=24.
          </Text>
          <Text style={styles.paragraph}>
            Example: EA = 36. E=30, A=6. (30+6=36)
          </Text>

          <Text style={styles.subTitle}>Reverse Sumerian</Text>
          <Text style={styles.paragraph}>
            Reverse Sumerian Cipher is a very advanced and special Cipher of the Gematria Calculator. The alphabets in this Cipher are written from A to Z but codes are in reverse form: A=156 and Z=6.
          </Text>
          <Text style={styles.paragraph}>
            Example: AS = 204. A=156, S=48. (156+48=204)
          </Text>

          <Text style={styles.subTitle}>Satanic</Text>
          <Text style={styles.paragraph}>
            Satanic Cipher in the Gematria calculator is quite easy to use but it has very different number codes. The code of A=36 and after this, as the alphabet series increases the codes also increase by adding +1: B=37, C=38.
          </Text>
          <Text style={styles.paragraph}>
            Example: AM = 84. A=36, M=48. (36+48=84)
          </Text>

          {/* ===== JEWISH ===== */}
          <Text style={styles.sectionTitle}>Jewish</Text>
          <Text style={styles.paragraph}>
            It has three types of Ciphers — Jewish Reduced, Jewish Ordinal, Jewish — with different number codes and Gematria Calculation Methods.
          </Text>

          <Text style={styles.subTitle}>Jewish Reduced</Text>
          <Text style={styles.paragraph}>
            This is an advanced Cipher of the Gematria Calculator. It has different number coding: A to I = 1 to 9, then J=24, then K to S = 1 to 9, after that T=1, U=2, V=7, W=9, X=3, Y=4, Z=5.
          </Text>

          <Text style={styles.subTitle}>Jewish Ordinal</Text>
          <Text style={styles.paragraph}>
            Jewish Ordinal in the Gematria Calculator is different from other ordinal Ciphers. In this Cipher the position of J, V, W is changed: J=24, V=25, W=27. It has 27 alphabet positions — the new character is "&" which comes at position 26.
          </Text>
          <Text style={styles.paragraph}>
            Example: THE& = 58. T=19, H=8, E=5, &=26. (19+8+5+26=58)
          </Text>

          <Text style={styles.subTitle}>Jewish</Text>
          <Text style={styles.paragraph}>
            All Jewish Ciphers have 27 number alphabets. Their number codes are: A to J = 1 to 9, K to S = 10 to 20, T to Z = 100 to 500, where some other characters have different unique codes: J=600, V=700, &=800, W=900.
          </Text>
          <Text style={styles.paragraph}>
            Example: WOW = 1850. W=900, O=50, W=900. (900+50+900=1850)
          </Text>

          {/* ===== KABBALAH ===== */}
          <Text style={styles.sectionTitle}>Kabbalah</Text>
          <Text style={styles.paragraph}>
            This is also called an advanced Cipher of the Gematria Calculator with different number coding and calculation functions. It also has some different categories with different number coding charts.
          </Text>

          <Text style={styles.subTitle}>ALW Kabbalah</Text>
          <Text style={styles.paragraph}>
            This is a very advanced Cipher of the Gematria Calculator, as it has very different number codes for every alphabet. It has unique number codes for the letters A, L, W: A=1, L=2, W=3.
          </Text>
          <Text style={styles.paragraph}>
            Example: TOM = 52. T=24, O=7, M=21. (24+7+21=52)
          </Text>

          <Text style={styles.subTitle}>KFW Kabbalah</Text>
          <Text style={styles.paragraph}>
            This is the second basic Cipher of the Kabbalah part of the Gematria Calculator. It has different number coding, and this Cipher also has special number codes for its name starting letters: K=1, F=2, W=3.
          </Text>
          <Text style={styles.paragraph}>
            Example: JERRY = 48. J=16, E=17, R=4, R=4, Y=7. (16+17+4+4+7=48)
          </Text>

          <Text style={styles.subTitle}>LCH Kabbalah</Text>
          <Text style={styles.paragraph}>
            This Cipher of the Gematria Calculator has different codes for every alphabet from A to Z, but also has different codes for L=1, C=2, H=3.
          </Text>
          <Text style={styles.paragraph}>
            Example: MCH = 26. M=21, C=2, H=3. (21+2+3=26)
          </Text>

          {/* ===== MATHEMATICAL ===== */}
          <Text style={styles.sectionTitle}>Mathematical</Text>
          <Text style={styles.paragraph}>
            In the Gematria Calculator, Mathematical Ciphers also have some different calculation formations with different codes for every alphabet.
          </Text>

          <Text style={styles.subTitle}>Primes</Text>
          <Text style={styles.paragraph}>
            This is an amazing Cipher of the Gematria Calculator. This Cipher has alphabet from A to Z and these 26 letters have number codes which are the first 26 prime numbers in mathematics, starting at 2 and ending at 101 (the 26th prime number).
          </Text>
          <Text style={styles.paragraph}>
            Example: APPLE = 156. A=2, P=53, P=53, L=37, E=11. (2+53+53+37+11=156)
          </Text>

          <Text style={styles.subTitle}>Trigonal</Text>
          <Text style={styles.paragraph}>
            This is also one of the best Ciphers of the Gematria Calculator. It is quite easy to understand — it has number codes with the special trigonal code formation method.
          </Text>
          <Text style={styles.paragraph}>
            Example: POT = 466. P=136, O=120, T=210. (136+120+210=466)
          </Text>

          <Text style={styles.subTitle}>Squares</Text>
          <Text style={styles.paragraph}>
            Squares Cipher has special codes with a special format: alphabets are written from A to Z with number order from 1 to 26, and in the number code formation of this Cipher, every number code is multiplied by the number of its order: A=1 then 1×1=1, B=2 so 2×2=4, where 4 is the final code for B.
          </Text>
          <Text style={styles.paragraph}>
            Example: CAT = 410. C=9, A=1, T=400. (9+1+400=410)
          </Text>

          {/* ===== OTHERS ===== */}
          <Text style={styles.sectionTitle}>Others</Text>
          <Text style={styles.paragraph}>
            These Ciphers are different in the Gematria Calculator but we have to know about all these Ciphers, so we can understand the working of the Gematria Calculator.
          </Text>

          <Text style={styles.subTitle}>Septenary</Text>
          <Text style={styles.paragraph}>
            This Cipher has divided all alphabets into two different parts where A to M and N to Z have number codes: A to G = 1 to 7, H to M = 6 to 1, N to T = 1 to 7, U to Z = 6 to 1.
          </Text>
          <Text style={styles.paragraph}>
            Example: DOG = 13. D=4, O=2, G=7. (4+2+7=13)
          </Text>

          <Text style={styles.subTitle}>Chaldean</Text>
          <Text style={styles.paragraph}>
            This Cipher of the Gematria calculator has all different codes for all alphabets.
          </Text>
          <Text style={styles.paragraph}>
            Example: TOP = 19. T=4, O=7, P=8. (4+7+8=19)
          </Text>

          {/* ===== EXCEPTIONS ===== */}
          <Text style={styles.sectionTitle}>Exceptions</Text>
          <Text style={styles.paragraph}>
            These Ciphers are basic but have exceptions with some letters and their number codes while calculating the value in the Gematria Calculator.
          </Text>

          <Text style={styles.subTitle}>Full Reduction Kv</Text>
          <Text style={styles.paragraph}>
            This Cipher has the same codes as Full Reduction, but it has an exception in alphabets K and V — these both have codes the same as in alphabetic order (their position in the alphabet table).
          </Text>
          <Text style={styles.paragraph}>
            Example: VAN = 28. V=22, A=1, N=5. (22+1+5=28)
          </Text>

          <Text style={styles.subTitle}>Single Reduction Kv</Text>
          <Text style={styles.paragraph}>
            This Cipher is also the same as Single Reduction but has an exception with two letters K and V: K=11, V=22.
          </Text>
          <Text style={styles.paragraph}>
            Example: KAS = 22. K=11, A=1, S=10. (11+1+10=22)
          </Text>

          <Text style={styles.subTitle}>Reverse Full Reduction EP</Text>
          <Text style={styles.paragraph}>
            In this Cipher of the Gematria Calculator, alphabets are written in a tablet form and have number codes from 1 to 9, but E and P are exceptions: E=22, P=11.
          </Text>
          <Text style={styles.paragraph}>
            Example: EAT = 37. E=22, A=8, T=7. (22+8+7=37)
          </Text>

          <Text style={styles.subTitle}>Reverse Single Reduction EP</Text>
          <Text style={styles.paragraph}>
            It is also the same as Reverse Single Reduction, and has codes the same as the Reverse Single Reduction Cipher but has exceptions in E and P: E=22, P=11.
          </Text>
          <Text style={styles.paragraph}>
            Example: PET = 40. P=11, E=22, T=7. (11+22+7=40)
          </Text>

          <Text style={styles.paragraph}>
            These are some basic and advanced Ciphers of the Gematria calculator. We have to keep all these points in mind when using this calculator. It is suggested that before using this calculator you understand the working and different coding formations of these calculators. The Gematria Calculator also has some other Ciphers but we have discussed only those Ciphers which are important and used in the different calculations.
          </Text>

          {/* ===== ABOUT US ===== */}
          <Text style={styles.sectionTitle}>About Us</Text>
          <Text style={styles.paragraph}>
            gematriacalculator.xyz can use not only English but a variety of alphabetic and numeric signs. The calculator is useful to turn the sense of words into useful meaning in the form of numbers. The calculator is very useful to provide an alternate numeric value for the Arabic variables. There are many common applications of the Gematria calculator. It has usefulness in Hebrew numerology. Another common application is calendar writing.
          </Text>
          <Text style={styles.paragraph}>
            gematriacalculator.xyz is one of the easiest available and usable options of all Gematria calculators available online. The calculator is also useful for people who use the technique for spiritual purposes. Other purposes such as biblical and historical translation and common calculation are also easy and convenient.
          </Text>

          {/* ===== BLOG ARTICLES ===== */}
          <Text style={styles.sectionTitle}>From Our Blog</Text>
          <Text style={styles.paragraph}>
            We publish in-depth articles exploring gematria history, cipher systems, practical applications, and the mathematical foundations of numerological traditions. Here are our latest articles:
          </Text>

          {blogHighlights.map((post, index) => (
            <TouchableOpacity
              key={index}
              style={styles.blogCard}
              onPress={() => Linking.openURL(post.url)}
            >
              <Text style={styles.blogTitle}>{post.title}</Text>
              <Text style={styles.blogMeta}>{post.date} — {post.author}</Text>
              <Text style={styles.blogExcerpt}>{post.excerpt}</Text>
              <Text style={styles.readMore}>Read More →</Text>
            </TouchableOpacity>
          ))}

          <TouchableOpacity
            style={styles.viewAllButton}
            onPress={() => Linking.openURL('/pages/blog.html')}
          >
            <Text style={styles.viewAllText}>View All Blog Articles →</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

const createStyles = (colors) => StyleSheet.create({
  container: {
    maxWidth: 900,
    width: '100%',
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 20,
    paddingHorizontal: 20,
  },
  containerMobile: {
    paddingHorizontal: 10,
  },
  toggleBar: {
    backgroundColor: colors.primarySurface,
    padding: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  toggleText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  content: {
    backgroundColor: colors.surface,
    padding: 20,
    borderRadius: 8,
    marginTop: 8,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
    marginTop: 15,
    marginBottom: 10,
    paddingBottom: 6,
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
  },
  subTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: colors.text,
    marginTop: 12,
    marginBottom: 6,
  },
  paragraph: {
    fontSize: 15,
    color: colors.textSecondary,
    lineHeight: 24,
    marginBottom: 10,
  },
  featureList: {
    marginBottom: 10,
  },
  featureItem: {
    fontSize: 15,
    color: colors.textSecondary,
    lineHeight: 24,
    marginBottom: 6,
    paddingLeft: 8,
  },
  blogCard: {
    backgroundColor: colors.mutedSurface,
    padding: 15,
    borderRadius: 8,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
  },
  blogTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 4,
  },
  blogMeta: {
    fontSize: 13,
    color: colors.lightText,
    marginBottom: 8,
  },
  blogExcerpt: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 21,
    marginBottom: 8,
  },
  readMore: {
    fontSize: 14,
    color: colors.primary,
    fontWeight: '600',
  },
  viewAllButton: {
    alignItems: 'center',
    marginTop: 10,
    padding: 12,
  },
  viewAllText: {
    fontSize: 16,
    color: colors.primary,
    fontWeight: '600',
  },
});
